import { useContext, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import DashboardCardHeader from "../DashboardCardHeader";
import { Box, CircularProgress, Container } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import {
  getHourAndMinutes,
  getYearDay,
} from "../../../../main/themes/defaultLayout";
import { DashboardContext } from "../../context/DashboardContexProvider";
import {
  BaseErrorCard,
  BaseUsinaValue,
} from "../../models/dados-indicadores.model";
import { EmptyTable } from "../../../clients/components/EmptyState";
import { CardTypesEnum } from "../../types";
import { getCardIsLoading } from "../TextCardContent";

export type AccumulatedCertifiedEnergyValueAndDateTime = {
  dateTime: string;
  value: number;
};

export type AccumulatedCertifiedEnergyProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: {
    generatedEnergy: AccumulatedCertifiedEnergyValueAndDateTime[];
    pFifty: number | number[];
    pSeventyFive: number | number[];
    pNinety: number | number[];
    pNinetyFive: number | number[];
    energiaCertificadaP50NormalizedValues: BaseUsinaValue[];
    energiaCertificadaP75NormalizedValues: BaseUsinaValue[];
    energiaCertificadaP90NormalizedValues: BaseUsinaValue[];
    energiaCertificadaP95NormalizedValues: BaseUsinaValue[];
    cardError: BaseErrorCard;
  };
};

export default function AccumulatedCertifiedEnergyChart({
  cardId,
  data,
  lastUpdate,
  cardType,
}: AccumulatedCertifiedEnergyProps) {
  const [isMouseIn, setIsMouseIn] = useState<boolean>(false);
  const accumulatedCertifiedEnergy = useRef(null);
  const headerTitle = "Energia Gerada x Energia Certificada";

  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;

  const {
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);

  const [energiaGeradaColor, setEnergiaGeradaColor] = useState<string>(
    cardsDisplayableColorProps.energiaAcumEnergiaCert.energiaGeradaColor ??
      "#FADE0A"
  );
  const [p50Color, setP50Color] = useState<string>(
    cardsDisplayableColorProps.energiaAcumEnergiaCert.P50Color ?? "#6D0DB8"
  );
  const [p75Color, setP75Color] = useState<string>(
    cardsDisplayableColorProps.energiaAcumEnergiaCert.P75Color ?? "#FAB900"
  );
  const [p90Color, setP90Color] = useState<string>(
    cardsDisplayableColorProps.energiaAcumEnergiaCert.P90Color ?? "#1A0144"
  );
  const [p95Color, setP95Color] = useState<string>(
    cardsDisplayableColorProps.energiaAcumEnergiaCert.P95Color ?? "#EE7D00"
  );

  const xLabels = data.generatedEnergy.map((item) =>
    getHourAndMinutes(item.dateTime)
  );
  const generatedEnergyValues = data.generatedEnergy.map((item) =>
    Number(item.value.toFixed(2))
  );

  const filterP50Normalized =
    data.energiaCertificadaP50NormalizedValues?.filter((item) =>
      data.generatedEnergy?.some((e) => e.dateTime === item.dateTime)
    );

  const pFiftyValues = filterP50Normalized
    ? filterP50Normalized.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const filterP75Normalized =
    data.energiaCertificadaP75NormalizedValues?.filter((item) =>
      data.generatedEnergy?.some((e) => e.dateTime === item.dateTime)
    );

  const pSeventyFiveValues = filterP75Normalized
    ? filterP75Normalized.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const filterP90Normalized =
    data.energiaCertificadaP90NormalizedValues?.filter((item) =>
      data.generatedEnergy?.some((e) => e.dateTime === item.dateTime)
    );

  const pNinetyValues = filterP90Normalized
    ? filterP90Normalized.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const filterP95Normalized =
    data.energiaCertificadaP95NormalizedValues?.filter((item) =>
      data.generatedEnergy?.some((e) => e.dateTime === item.dateTime)
    );

  const pNinetyFiveValues = filterP95Normalized
    ? filterP95Normalized.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const collectDay =
    data.generatedEnergy.length > 0
      ? getYearDay(data.generatedEnergy[0].dateTime)
      : "";
  const optionsEnergy: EChartsOption = {
    title: {
      text: collectDay,
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    backgroundColor: "transparent",

    tooltip: {
      trigger: "axis",
      confine: true,
    },
    legend: {
      top: "5%",
      left: "right",
      data: ["Energia Gerada", "P50", "P75", "P90", "P95"],
    },
    xAxis: {
      data: xLabels,
    },
    yAxis: {},
    series: [
      {
        name: "P50",
        type: "line",
        data: pFiftyValues,
        itemStyle: {
          color: p50Color,
        },
      },
      {
        name: "P75",
        type: "line",
        data: pSeventyFiveValues,
        itemStyle: {
          color: p75Color,
        },
      },
      {
        name: "P90",
        type: "line",
        data: pNinetyValues,
        itemStyle: {
          color: p90Color,
        },
      },
      {
        name: "P95",
        type: "line",
        data: pNinetyFiveValues,
        itemStyle: {
          color: p95Color,
        },
      },
      {
        name: "Energia Gerada",
        type: "bar",
        data: generatedEnergyValues,
        itemStyle: {
          color: energiaGeradaColor,
        },
      },
    ],
  };

  const [openModal, setopenModal] = useState<boolean>(false);

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      energiaAcumEnergiaCert: {
        energiaGeradaColor: energiaGeradaColor,
        P50Color: p50Color,
        P75Color: p75Color,
        P90Color: p90Color,
        P95Color: p95Color,
      },
    });
    setopenModal(false);
  };

  const hasData =
    generatedEnergyValues.length > 0 ||
    pNinetyFiveValues.length > 0 ||
    pNinetyValues.length > 0 ||
    pSeventyFiveValues.length > 0 ||
    pFiftyValues.length > 0;

  return (
    <>
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          cardId={cardId}
          elementRef={accumulatedCertifiedEnergy}
          isMouseIn={isMouseIn}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          colorButton
          onConfirmColorsModal={handleConfirmModal}
          colorsOptions={[
            {
              title: "Energia Gerada",
              value: energiaGeradaColor,
              setValue: setEnergiaGeradaColor,
            },
            {
              title: "P50",
              value: p50Color,
              setValue: setP50Color,
            },
            {
              title: "P75",
              value: p75Color,
              setValue: setP75Color,
            },
            {
              title: "P90",
              value: p90Color,
              setValue: setP90Color,
            },
            {
              title: "P95",
              value: p95Color,
              setValue: setP95Color,
            },
          ]}
          cardType={CardTypesEnum.EnergiaAcumEnergiaCert}
          hasFilter
        />
        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData ? (
            <Container
              ref={accumulatedCertifiedEnergy}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "90%",
              }}
            >
              <RopChart option={optionsEnergy} />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable title="Nenhum dado encontrado!" />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </div>
    </>
  );
}
